import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ContentTypes } from 'src/app/shared/enums/content-types';
import { FORM_FIELDS } from '../constants/form-fields';
import { FORM_PATHS } from '../constants/form-paths';
import { BehaviorSubject, EMPTY, Observable, Subject, of } from 'rxjs';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { FormBuilder, Validators } from '@angular/forms';
import { AuthorService } from 'src/app/shared/services/author.service';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { WithDeactivateGuard } from 'src/app/shared/mixins/with-deactivate-guard';
import { WithNextStateFormSection } from 'src/app/shared/mixins/with-next-states-form-section';
import { WithAuthorsFormSection } from 'src/app/shared/mixins/with-authors-form-section';
import { WithPublishTimeFormSection } from 'src/app/shared/mixins/with-publish-time';
import { WithTaggingFormSection } from 'src/app/shared/mixins/with-tagging-form-section';
import { WithRelatedDataFormSection } from 'src/app/shared/mixins/with-related-data-form-section';
import { ManagerService } from 'src/app/shared/services/manager.service';
import { SharedContentTypeService } from 'src/app/content/services/shared-content-type.service';
import { UrlService } from 'src/app/shared/services/url.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserSettingsService } from 'src/app/shared/services/user-settings.service';
import { DigitalPublicationContentTypeService } from 'src/app/content/services/digital-publication-content-type.service';
import { DigitalPublicationContentDto } from 'src/app/core/dtos/digital-publication-content-dto';
import {
  catchError,
  filter,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { IErrorResponse } from 'src/app/core/dtos/interfaces/i-error-response';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { IDigitalPublicationContentDto } from 'src/app/core/dtos/interfaces/i-digital-publication-content-dto';
import { WithHeadlineFormSection } from 'src/app/shared/mixins/with-headline-form-section';
import { IAllowableState } from 'src/app/core/dtos/interfaces/i-allowable-state';
import { IManager } from 'src/app/core/models/i-manager';
import { IFund } from 'src/app/core/models/i-fund';
import { ISector } from 'src/app/core/models/i-sector';
import { IAssetClass } from 'src/app/core/models/i-asset-class';
import { IEtf } from 'src/app/core/models/i-etf';
import { IInvestmentTrust } from 'src/app/core/models/i-investment-trust';
import { IShare } from 'src/app/core/models/i-share';
import { ITag } from 'src/app/core/models/i-tag';
import { ITopic } from 'src/app/core/models/i-topic';
import { ICompany } from 'src/app/core/models/i-company';
import { IPerson } from 'src/app/core/models/i-person';
import { IAuthor } from 'src/app/core/models/i-author';
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { ISaveContentResponseDto } from 'src/app/core/dtos/interfaces/i-save-content-response-dto';
import { WithUrlFormSection } from 'src/app/shared/mixins/with-url-form-section';
import { WithProductFormSection } from 'src/app/shared/mixins/with-product-form-section';
import { WithPlatformFormSection } from 'src/app/shared/mixins/with-platform-form-section';
import { WithClientFormSection } from 'src/app/shared/mixins/with-client-form-section';
import { TIMEZONES } from 'src/app/shared/constants/timezones';
import { DigitalPublicationDataService } from 'src/app/content-management/shared/services/digital-publication-data.service';
import { IDigitalPublicationSection } from 'src/app/core/dtos/interfaces/i-digital-publication-section';
import { WithDigitalPublicationSections } from 'src/app/shared/mixins/with-digital-publication-sections';
import { RequireMatch } from 'src/app/shared/utils/require-match';
import { SignalrService } from 'src/app/content-management/shared/services/signalr.service';
import { sign } from 'crypto';
import { TopicService } from 'src/app/content-management/shared/services/topic.service';

class DigitalPublicationContentFormComponentBase {
  FORM_PATHS = FORM_PATHS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  form = this.formBuilder.group({});

  constructor(
    public formBuilder: FormBuilder,
    public _dialogService: DialogService,
    public managerService: ManagerService,
    public authorService: AuthorService,
    public digitalPublicationDataService: DigitalPublicationDataService,
    public signalRService: SignalrService,
    public topicService: TopicService
  ) {}
}

const DigitalPublicationContentFormComponentMixinBase = WithProcessing(
  WithDeactivateGuard(
    WithHeadlineFormSection(
      WithNextStateFormSection(
        WithAuthorsFormSection(
          WithPublishTimeFormSection(
            WithTaggingFormSection(
              WithRelatedDataFormSection(
                WithUrlFormSection(
                  WithPlatformFormSection(
                    WithProductFormSection(
                      WithClientFormSection(
                        WithDigitalPublicationSections(
                          DigitalPublicationContentFormComponentBase
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  )
);

@Component({
  selector: 'app-digital-publication-content-form',
  templateUrl: './digital-publication-content-form.component.html',
  styleUrls: ['./digital-publication-content-form.component.scss'],
})
export class DigitalPublicationContentFormComponent
  extends DigitalPublicationContentFormComponentMixinBase
  implements OnInit, OnDestroy
{
  FORM_FIELDS = FORM_FIELDS;
  FORM_PATHS = FORM_PATHS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  formReady = new BehaviorSubject<boolean>(false);

  isSaving = false;
  isLoading = false;
  isProcessingStateChange = false;
  isEditMode = false;
  isNewDigitalPublication = false;

  id: string;
  pageHeader: string;
  digitalPublicationContent$!: Observable<IDigitalPublicationContentDto>;
  initialDigitalPublicationContent!: IDigitalPublicationContentDto;
  currentUserName: string = '';

  form = this.formBuilder.group({
    [this.FORM_FIELDS.HEADLINE]: ['', Validators.required],
    [this.FORM_FIELDS.URL]: ['', Validators.required],
    [this.FORM_FIELDS.PLATFORM]: ['', RequireMatch],
    [this.FORM_FIELDS.PRODUCT]: ['', RequireMatch],
    [this.FORM_FIELDS.CLIENT]: ['', RequireMatch],
    [this.FORM_FIELDS.RELATED_MANAGERS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.RELATED_FUNDS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.RELATED_SECTORS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.RELATED_ASSET_CLASSES]: this.formBuilder.array([]),
    [this.FORM_FIELDS.RELATED_ETFS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.RELATED_INVESTMENT_TRUSTS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.RELATED_SHARES]: this.formBuilder.array([]),
    [this.FORM_FIELDS.SELECTED_AUTHORS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.SURTITLES]: this.formBuilder.array([]),
    [this.FORM_FIELDS.TAGS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.TOPICS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.TOPICS_WITH_TRANSLATIONS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.COMPANIES]: this.formBuilder.array([]),
    [this.FORM_FIELDS.PEOPLE]: this.formBuilder.array([]),
    [this.FORM_FIELDS.PUBLISH]: this.formBuilder.group(
      {
        [this.FORM_FIELDS.DATE]: [],
        [this.FORM_FIELDS.TIMEZONE]: [],
        [this.FORM_FIELDS.SHOW_PUBLISH_TIME]: [true],
      },
      {
        validators: [this._initPublishTimeValidation()],
      }
    ),
    [this.FORM_FIELDS.INTERNAL_INFORMATION]: this.formBuilder.group({
      [this.FORM_FIELDS.LIVE_URLS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.CONTENT_IDS]: [],
    }),
    [this.FORM_FIELDS.ARTICLE_VERSION_LIST]: this.formBuilder.array([]),
    [this.FORM_FIELDS.NEXT_STATES]: [],
    [this.FORM_FIELDS.SECTIONS]: this.formBuilder.array(
      [],
      this._initSectionValidation()
    ),
  });

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: Event): void {
    this.handleBeforeUnload(event);
  }

  constructor(
    public formBuilder: FormBuilder,
    public digitalContentService: DigitalPublicationContentTypeService,
    public sharedService: SharedContentTypeService,
    public _dialogService: DialogService,
    public authorService: AuthorService,
    public managerService: ManagerService,
    public signalRService: SignalrService,
    public topicService: TopicService,
    public urlService: UrlService,
    public digitalPublicationDataService: DigitalPublicationDataService,
    private _activatedRoute: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _userSettingsService: UserSettingsService
  ) {
    super(
      formBuilder,
      _dialogService,
      managerService,
      authorService,
      digitalPublicationDataService,
      signalRService,
      topicService
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngOnInit(): void {
    this._routerUrlSub();

    if (this.isNewDigitalPublication) {
      this.pageHeader = 'Create Digital Publication';
      this.initialDigitalPublicationContent = new DigitalPublicationContentDto(
        {}
      );
      this.initialDigitalPublicationContent.dynamicData.allowableNextStates =
        [];
      this.initialDigitalPublicationContent.dynamicData.versions = [];
      this.initialDigitalPublicationContent.timezoneIdentifier = TIMEZONES.find(
        (t) => t.isDefault
      ).windowsTimezoneId;

      this._userSettingsService.getForCurrentUser$().subscribe((x) => {
        this.initialDigitalPublicationContent.authors =
          x.defaultArticleAuthors ?? [];

        this._initForm();
      });
    } else {
      this.pageHeader = 'Edit Digital Publication';
      this._routeParamsSub();
    }

    if (!this.isEditMode && !this.isNewDigitalPublication) {
      this.form.disable();
    }

    this._userSettingsService.getCurrentUserName$().subscribe((username) => {
      this.currentUserName = username;
    });
  }

  private _routerUrlSub(): void {
    this._activatedRoute.url.subscribe((path) => {
      const segments = path.map((segment) => segment.path);
      this.isEditMode =
        segments.length > 0 &&
        (segments.includes('edit') || segments.includes('new'));

      this.isNewDigitalPublication =
        segments.length > 0 && segments.includes('new');
    });
  }

  private _routeParamsSub(): void {
    this._activatedRoute.paramMap
      .pipe(
        filter((paramMaps) => {
          return !!paramMaps.get('id');
        }),
        switchMap((paramsMap) => {
          this.id = paramsMap.get('id');
          return this.process$(this.getDigitalPublicationContent$(this.id));
        })
      )
      .pipe(
        catchError((error: IErrorResponse) => {
          if (error.status === 404) {
            this._dialogService.openErrorDialogAndNavigate(
              {
                title: 'Digital publication not found',
                message:
                  'The digital publication specified was not found. It may have been deleted.',
              },
              'dashboard'
            );
          } else {
            this._dialogService.openErrorDialogAndNavigate(
              {
                message:
                  'An error has occurred loading the digital publication. Please contact IT.',
              },
              'dashboard'
            );
          }

          return EMPTY;
        })
      )
      .subscribe({
        next: (digitalPublicationContent: IDigitalPublicationContentDto) => {
          this.initialDigitalPublicationContent = digitalPublicationContent;

          this.setEditMode(digitalPublicationContent.dynamicData.canEdit);
          this._initForm();
        },
      });
  }

  private getDigitalPublicationContent$(id: string) {
    if (this._isVersionId(id)) {
      this.digitalPublicationContent$ = this.process$(
        this.digitalContentService.getByVersionId$(id)
      );
    } else {
      this.digitalPublicationContent$ = this.process$(
        this.digitalContentService.getById$(id)
      );
    }

    return this.digitalPublicationContent$;
  }

  private setEditMode(newEditModeValue) {
    if (newEditModeValue !== this.isEditMode) {
      newEditModeValue
        ? this.redirectToEditContent(this.id)
        : this.redirectToReadOnlyContent(this.id);
      return;
    }
    this.isEditMode = newEditModeValue;

    this.isEditMode ? this.form.enable() : this.form.disable();
  }

  private redirectToReadOnlyContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.DIGITAL_PUBLICATION],
      id,
    ]);
  }

  private redirectToEditContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.DIGITAL_PUBLICATION],
      id,
      'edit',
    ]);
  }

  private _isVersionId(contentIdToCheck: string): boolean {
    return contentIdToCheck.includes('DigitalPublication');
  }

  private _initForm(): void {
    this._initHeadline(this.initialDigitalPublicationContent.headline);
    this._initUrl(this.initialDigitalPublicationContent.url);
    this._initProduct(this.initialDigitalPublicationContent.product);
    this._initPlatform(this.initialDigitalPublicationContent.platform);
    this._initClient(this.initialDigitalPublicationContent.client);
    this._initNextStates(
      this.initialDigitalPublicationContent.dynamicData.allowableNextStates
    );
    this._initPublishTime(
      this.initialDigitalPublicationContent.presetPublishDate,
      this.initialDigitalPublicationContent.timezoneIdentifier
    );

    this._initAuthors(this.initialDigitalPublicationContent.authors);
    this._initTagging(
      this.initialDigitalPublicationContent.tags,
      this.initialDigitalPublicationContent.topics,
      this.initialDigitalPublicationContent.relatedCompanies,
      this.initialDigitalPublicationContent.relatedPeople,
      this.topicService
    );

    this._initSections(this.initialDigitalPublicationContent.sections);

    this._initRelatedData(
      this.initialDigitalPublicationContent.relatedManagers,
      this.initialDigitalPublicationContent.relatedFunds,
      this.initialDigitalPublicationContent.relatedSectors,
      this.initialDigitalPublicationContent.relatedAssetClasses,
      this.initialDigitalPublicationContent.relatedEtfs,
      this.initialDigitalPublicationContent.relatedInvestmentTrusts,
      this.initialDigitalPublicationContent.relatedShares
    );

    this._initFormValueChanges();
    this.formReady.next(true);
  }

  private _initFormValueChanges(): void {
    this._initHeadlineValueChanges(this.getHeadline);
    this._initUrlValueChanges(this.getUrl);
    this._initPlatformValueChanges(this.getPlatform);
    this._initProductValueChanges(this.getProduct);
    this._initClientValueChanges(this.getClient);
    this._initNextStatesValueChanges(this.getNextStates);
    this._initPublishTimeValueChanges(
      this.getPresetPublishTime,
      this.getTimezone
    );

    this._initRelatedDataValueChanges(
      this.getRelatedManagers,
      this.getRelatedFunds,
      this.getRelatedSectors,
      this.getRelatedAssetClasses,
      this.getRelatedEtfs,
      this.getRelatedInvestmentTrusts,
      this.getRelatedShares
    );

    this._initTaggingValueChanges(
      this.getTags,
      this.getTopics,
      this.getCompanies,
      this.getPeople
    );

    this._initSelectedAuthorsValueChanges(this.getSelectedAuthors);

    this._initSectionsValueChanges(this.getSections);
  }

  getHeadline = (): string | null => {
    return this.initialDigitalPublicationContent.headline;
  };

  getUrl = (): string | null => {
    return this.initialDigitalPublicationContent.url;
  };

  getProduct = (): string | null => {
    return this.initialDigitalPublicationContent.product;
  };

  getPlatform = (): string | null => {
    return this.initialDigitalPublicationContent.platform;
  };

  getClient = (): string | null => {
    return this.initialDigitalPublicationContent.client;
  };

  getNextStates = (): IAllowableState[] | null => {
    return this.initialDigitalPublicationContent.dynamicData
      .allowableNextStates;
  };

  getPresetPublishTime = (): Date | null => {
    return this.initialDigitalPublicationContent.presetPublishDate;
  };

  getTimezone = (): string | null => {
    return this.initialDigitalPublicationContent.timezoneIdentifier;
  };

  getRelatedManagers = (): IManager[] | null => {
    return this.initialDigitalPublicationContent.relatedManagers;
  };

  getRelatedFunds = (): IFund[] | null => {
    return this.initialDigitalPublicationContent.relatedFunds;
  };

  getRelatedSectors = (): ISector[] | null => {
    return this.initialDigitalPublicationContent.relatedSectors;
  };

  getRelatedAssetClasses = (): IAssetClass[] | null => {
    return this.initialDigitalPublicationContent.relatedAssetClasses;
  };

  getRelatedEtfs = (): IEtf[] | null => {
    return this.initialDigitalPublicationContent.relatedEtfs;
  };

  getRelatedInvestmentTrusts = (): IInvestmentTrust[] | null => {
    return this.initialDigitalPublicationContent.relatedInvestmentTrusts;
  };

  getRelatedShares = (): IShare[] | null => {
    return this.initialDigitalPublicationContent.relatedShares;
  };

  getTags = (): ITag[] | null => {
    return this.initialDigitalPublicationContent.tags;
  };

  getTopics = (): ITopic[] | null => {
    return this.initialDigitalPublicationContent.topics;
  };

  getCompanies = (): ICompany[] | null => {
    return this.initialDigitalPublicationContent.relatedCompanies;
  };

  getPeople = (): IPerson[] | null => {
    return this.initialDigitalPublicationContent.relatedPeople;
  };

  getSelectedAuthors = (): IAuthor[] | null => {
    return this.initialDigitalPublicationContent.authors;
  };

  getSections = (): IDigitalPublicationSection[] | null => {
    return this.initialDigitalPublicationContent.sections;
  };

  readonly getContentFn$ = (id: string) => {
    return this.getDigitalPublicationContent$(id);
  };

  readonly createNewVersionFn$ = (contentId, versionId) =>
    this.digitalContentService.createVersion$(contentId, versionId);

  readonly deleteVersionFn$ = (contentId, versionId) =>
    this.digitalContentService.deleteVersion$(contentId, versionId);

  readonly createFn$ = (content) => {
    return this.digitalContentService.create$(content);
  };

  readonly updateFn$ = (id: string, content: IDigitalPublicationContentDto) => {
    return this.digitalContentService.update$(id, content);
  };

  readonly deleteFn$ = (contentId: string) => {
    return this.digitalContentService.delete$(contentId);
  };

  readonly spikeFn$ = (contentId: string) => {
    return this.digitalContentService.spike$(contentId);
  };

  onCreateNewVersion(): void {
    this.isProcessingStateChange = true;

    this.sharedService
      .createNewVersionWithCurrentStateCheck$(
        this.createNewVersionFn$,
        this.deleteVersionFn$,
        this.initialDigitalPublicationContent.contentId,
        this.initialDigitalPublicationContent.id,
        this.initialDigitalPublicationContent.dynamicData.currentDraftVersion
      )
      .subscribe(() => {
        this._snackBar.open(
          `New digital publication version created successfully`,
          null,
          {
            duration: 5 * 1000,
          }
        );
        this.isProcessingStateChange = false;
        this.redirectToEditContent(
          this.initialDigitalPublicationContent.contentId
        );
      });
  }

  onUpdateState(stateAction: ContentStatus): void {
    this.isProcessingStateChange = true;
    const updateStateFn = (
      initialDigitalPublicationContentId: string,
      action: string
    ) =>
      this.digitalContentService.updateState$(
        initialDigitalPublicationContentId,
        action
      );

    const setEditModeFn = (newEditModeValue: boolean) =>
      this.setEditMode(newEditModeValue);

    this.sharedService
      .updateState<IDigitalPublicationContentDto>(
        updateStateFn,
        this.getContentFn$,
        setEditModeFn,
        this.initialDigitalPublicationContent.id,
        stateAction,
        this.isEditMode
      )
      .pipe(
        tap((refreshedDto: IDigitalPublicationContentDto) => {
          this.initialDigitalPublicationContent = refreshedDto;
        }),
        finalize(() => (this.isProcessingStateChange = false))
      )
      .subscribe();
  }

  onSave(): void {
    if (this.isNewDigitalPublication) {
      this.isSaving = true;

      this.digitalContentService
        .create$(this.buildDigitalPublication())
        .pipe(
          catchError(() => {
            this._dialogService.openErrorDialog({
              message:
                'An error has occurred whilst saving the content. Please contact IT.',
            });
            return EMPTY;
          }),
          finalize(() => (this.isSaving = false))
        )
        .subscribe((x: ISaveContentResponseDto) => {
          this.form.markAsPristine();
          this._router.navigate([
            '/content',
            ContentTypeRouterPaths[ContentTypes.DIGITAL_PUBLICATION],
            x.contentId,
            'edit',
          ]);
        });
    } else {
      this.isSaving = true;

      this.sharedService
        .saveContent<IDigitalPublicationContentDto>(
          this.updateFn$,
          this.getContentFn$,
          null,
          [],
          [],
          of([]),
          this.buildDigitalPublication()
        )
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe((refreshedDto: IDigitalPublicationContentDto) => {
          this._snackBar.open('Digital publication saved successfully', null, {
            duration: 5 * 1000,
          });
          this.initialDigitalPublicationContent = refreshedDto;
          this.form.markAsPristine();
          this.signalRService.userSavedPage(
            this.currentUserName,
            refreshedDto.contentId
          );
        });
    }
  }

  onDelete() {
    this.sharedService
      .deleteContent$(
        this.deleteFn$,
        this.initialDigitalPublicationContent.contentId
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  onSpike() {
    this.sharedService
      .spikeContent$(
        this.spikeFn$,
        this.initialDigitalPublicationContent.contentId
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  onReactivate(): void {
    this.isProcessingStateChange = true;

    this.sharedService
      .reactivateContent(
        this.getContentFn$,
        this.createNewVersionFn$,
        this.deleteVersionFn$,
        this.initialDigitalPublicationContent.contentId,
        this.initialDigitalPublicationContent.id,
        this.initialDigitalPublicationContent.dynamicData
          .latestPublishedVersionId,
        [],
        this.initialDigitalPublicationContent.dynamicData.currentDraftVersion,
        of([]),
        this.isPreviouslyPublishedVersion()
      )
      .pipe(finalize(() => (this.isProcessingStateChange = false)))
      .subscribe({
        next: () => {
          this._snackBar.open(
            `Digital Publication reactivated successfully`,
            null,
            {
              duration: 5 * 1000,
            }
          );
          this.redirectToEditContent(
            this.initialDigitalPublicationContent.contentId
          );
        },
      });
  }

  private isPreviouslyPublishedVersion(): boolean {
    return (
      this.initialDigitalPublicationContent.isPublished() &&
      this.initialDigitalPublicationContent?.dynamicData
        ?.latestPublishedVersionId !== this.initialDigitalPublicationContent?.id
    );
  }

  handleLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  deleteUnpublishedDraft() {
    this.sharedService
      .deleteUnpublishedDraft$(
        this.deleteVersionFn$,
        this.initialDigitalPublicationContent.contentId,
        this.initialDigitalPublicationContent.id
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  private buildDigitalPublication(): IDigitalPublicationContentDto {
    const newDigitalPublication = new DigitalPublicationContentDto({});
    Object.assign(newDigitalPublication, this.initialDigitalPublicationContent);
    newDigitalPublication.headline = this._buildHeadline();
    newDigitalPublication.url = this._buildUrl();
    newDigitalPublication.platform = this._buildPlatform();
    newDigitalPublication.product = this._buildProduct();
    newDigitalPublication.client = this._buildClient();
    newDigitalPublication.presetPublishDate = this._buildPresetPublishTime();
    newDigitalPublication.timezoneIdentifier = this._buildTimezone();
    newDigitalPublication.relatedManagers = this._buildRelatedManagers();
    newDigitalPublication.relatedFunds = this._buildRelatedFunds();
    newDigitalPublication.relatedSectors = this._buildRelatedSectors();
    newDigitalPublication.relatedAssetClasses =
      this._buildRelatedAssetClasses();
    newDigitalPublication.relatedEtfs = this._buildRelatedEtfs();
    newDigitalPublication.relatedInvestmentTrusts =
      this._buildRelatedInvestmentTrusts();
    newDigitalPublication.relatedShares = this._buildRelatedShares();
    newDigitalPublication.authors = this._buildSelectedAuthors();
    newDigitalPublication.topics = this._buildTopics();
    newDigitalPublication.tags = this._buildTags();
    newDigitalPublication.relatedCompanies = this._buildCompanies();
    newDigitalPublication.relatedPeople = this._buildPeople();
    newDigitalPublication.sections = this._buildSections();

    return newDigitalPublication;
  }
}
